import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, getInsideChrome, insideIsDark, rgbaFromHex } from '@utils/colorSystem';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

/**
 * Segmented control: selected segment uses full rounded primary fill (all four corners), not a full-bleed sliding bar.
 * Tab columns share the width of the widest label.
 */
export const SliderTab = ({
    items = [],
    activeKey,
    onChange,
    defaultActiveKey,
    fitContent = false,
    $navMaxContent = false,
    $multilineTabLabels = false,
    ...rest
}) => {
    const [internalKey, setInternalKey] = useState(defaultActiveKey ?? items[0]?.key);
    const resolvedKey = activeKey !== undefined ? activeKey : internalKey;
    const activeIndex = useMemo(() => {
        const i = items.findIndex((it) => it.key === resolvedKey);
        return i >= 0 ? i : 0;
    }, [items, resolvedKey]);

    const itemsSig = useMemo(() => items.map((i) => `${i.key}:${String(i.label)}`).join('|'), [items]);

    const trackRef = useRef(null);
    const tabRefs = useRef([]);
    const widthMeterRef = useRef(null);
    const [equalTabWidth, setEqualTabWidth] = useState(0);

    const fullWidth = !fitContent && !$navMaxContent;

    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);

    const updateScrollButtons = useCallback(() => {
        if (!trackRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
        setShowLeftScroll(scrollLeft > 0);
        setShowRightScroll(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }, []);

    useLayoutEffect(() => {
        updateScrollButtons();
        window.addEventListener('resize', updateScrollButtons);
        return () => window.removeEventListener('resize', updateScrollButtons);
    }, [updateScrollButtons, itemsSig, equalTabWidth]);

    const scrollBy = (amount) => {
        if (trackRef.current) {
            trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
            setTimeout(updateScrollButtons, 300);
        }
    };

    /** Measure widest label off-layout so parent flex never squeezes "Inactive" before we know its width. */
    const measureLabelWidths = useCallback(() => {
        const root = widthMeterRef.current;
        if (!root) return;
        const cells = root.querySelectorAll('[data-slider-tab-meter]');
        let max = 0;
        cells.forEach((el) => {
            max = Math.max(max, el.getBoundingClientRect().width);
        });
        if (max > 0) setEqualTabWidth(Math.ceil(max));
    }, []);

    useLayoutEffect(() => {
        setEqualTabWidth(0);
    }, [itemsSig]);

    useLayoutEffect(() => {
        measureLabelWidths();
        const id = requestAnimationFrame(() => measureLabelWidths());
        return () => cancelAnimationFrame(id);
    }, [measureLabelWidths, itemsSig]);

    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined') return undefined;
        const el = widthMeterRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => measureLabelWidths());
        ro.observe(el);
        return () => ro.disconnect();
    }, [measureLabelWidths, itemsSig]);

    const handleSelect = useCallback(
        (key) => {
            onChange?.(key);
            if (activeKey === undefined) setInternalKey(key);
        },
        [onChange, activeKey],
    );

    const onKeyDown = useCallback(
        (e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            const dir = e.key === 'ArrowRight' ? 1 : -1;
            const len = items.length;
            if (!len) return;
            let next = activeIndex;
            for (let step = 0; step < len; step += 1) {
                next = (next + dir + len) % len;
                if (!items[next]?.disabled) break;
            }
            if (items[next]?.disabled) return;
            handleSelect(items[next].key);
            requestAnimationFrame(() => tabRefs.current[next]?.focus());
        },
        [activeIndex, items, handleSelect],
    );

    const measuring = equalTabWidth === 0;
    const tabFlex = measuring ? '0 0 auto' : fullWidth ? '1 1 0' : '0 0 auto';
    const tabStyle =
        !measuring && equalTabWidth > 0
            ? fullWidth
                ? { minWidth: equalTabWidth, boxSizing: 'border-box' }
                : {
                      minWidth: equalTabWidth,
                      width: equalTabWidth,
                      flexShrink: 0,
                      boxSizing: 'border-box',
                  }
            : undefined;

    if (!items.length) return null;

    return (
        <Root
            {...rest}
            $fitContent={fitContent}
            $navMaxContent={$navMaxContent}
            role="tablist"
            aria-orientation="horizontal"
            onKeyDown={onKeyDown}
        >
            <ScrollWrapper>
                {showLeftScroll && (
                    <ScrollArrow type="button" $left onClick={() => scrollBy(-200)}>
                        <LeftOutlined />
                    </ScrollArrow>
                )}
                <Track ref={trackRef} $fullWidth={fullWidth} onScroll={updateScrollButtons}>
                    <SliderTabWidthMeter ref={widthMeterRef} aria-hidden>
                        {items.map((item) => (
                            <SliderTabMeterCell key={item.key} data-slider-tab-meter>
                                {item.label}
                            </SliderTabMeterCell>
                        ))}
                    </SliderTabWidthMeter>
                    {items.map((item, index) => {
                        const selected = index === activeIndex;
                        return (
                            <TabButton
                                key={item.key}
                                ref={(el) => {
                                    tabRefs.current[index] = el;
                                }}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                tabIndex={selected ? 0 : -1}
                                $selected={selected}
                                $multilineTabLabels={$multilineTabLabels}
                                $tabFlex={tabFlex}
                                style={tabStyle}
                                onClick={() => !item.disabled && handleSelect(item.key)}
                                disabled={item.disabled}
                            >
                                {item.label}
                            </TabButton>
                        );
                    })}
                </Track>
                {showRightScroll && (
                    <ScrollArrow type="button" $right onClick={() => scrollBy(200)}>
                        <RightOutlined />
                    </ScrollArrow>
                )}
            </ScrollWrapper>
        </Root>
    );
};

/** Off-screen flex row: same typography/padding as tabs → natural width per label; max drives equal column width. */
const SliderTabWidthMeter = styled.div`
    position: fixed;
    left: 0;
    top: 0;
    z-index: -1;
    width: max-content;
    max-width: none;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    pointer-events: none;
    visibility: hidden;
    box-sizing: border-box;
`;

const SliderTabMeterCell = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    flex: none;
    margin: 0;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 500;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;

    @media (max-width: 1024px) {
        padding: 8px 14px;
    }

    @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 13px;
        max-width: 150px;
    }

    @media (max-width: 480px) {
        padding: 6px 10px;
        font-size: 12px;
        max-width: 120px;
    }
`;

const Root = styled.div`
    width: ${({ $fitContent, $navMaxContent }) => {
        if ($navMaxContent) return 'max-content';
        return $fitContent ? 'fit-content' : '100%';
    }};
    max-width: ${({ $navMaxContent }) => ($navMaxContent ? 'none' : '100%')};
    min-width: 0;
    box-sizing: border-box;
`;

export const ScrollWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

export const ScrollArrow = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    ${({ $left }) => ($left ? 'left: 4px;' : 'right: 4px;')}
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ theme }) => (insideIsDark(theme) ? '#333' : '#fff')};
    border: 1px solid ${({ theme }) => (insideIsDark(theme) ? '#444' : '#e5e5e5')};
    color: ${({ theme }) => (insideIsDark(theme) ? '#fff' : '#333')};
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    outline: none;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ theme }) => (insideIsDark(theme) ? '#444' : '#f5f5f5')};
        transform: translateY(-50%) scale(1.1);
    }
`;

const Track = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 0;
    gap: 0;
    border-radius: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
    min-height: 40px;

    ${({ theme }) => {
        const dark = insideIsDark(theme);
        const c = colors;
        const chrome = getInsideChrome(theme);
        if (dark) {
            return css`
                background: ${chrome.mutedControlBg};
                border: 1px solid ${chrome.mutedControlBorder};
                box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
            `;
        }
        return css`
            background: ${c.neutral[0]};
            border: 1px solid ${c.neutral[200]};
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        `;
    }}

    @media (max-width: 768px) {
        border-radius: 10px;
    }
`;

const TabButton = styled.button`
    position: relative;
    z-index: 1;
    flex: ${({ $tabFlex }) => $tabFlex || '0 0 auto'};
    box-sizing: border-box;
    margin: 0;
    padding: 10px 16px;
    border: none;
    border-radius: 0;
    
    &:first-of-type {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
    }

    &:last-of-type {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
    }

    font-size: 14px;
    font-weight: 500;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    line-height: 1.35;
    text-align: center;
    cursor: pointer;
    transition:
        background-color 0.22s ease,
        color 0.22s ease,
        box-shadow 0.22s ease;

    ${({ $selected, theme }) => {
        const dark = insideIsDark(theme);
        const c = colors;
        if ($selected) {
            return css`
                background: #7543DC !important;
                color: ${c.neutral[0]} !important;
                box-shadow: none;
            `;
        }
        return css`
            background: transparent !important;
            color: ${dark ? 'rgba(255, 255, 255, 0.55)' : c.neutral[500]};
            box-shadow: none;
        `;
    }}

    white-space: nowrap !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;

    &:focus-visible {
        outline: 2px solid #7543DC;
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        ${({ theme, $selected }) => {
            const dark = insideIsDark(theme);
            const c = colors;
            if ($selected) {
                return css`
                    background: #7543DC !important;
                    color: ${c.neutral[0]} !important;
                    box-shadow: none;
                `;
            }
            if (dark) {
                return css`
                    color: rgba(255, 255, 255, 0.9);
                    background: ${rgbaFromHex(c.neutral[0], 0.06)} !important;
                `;
            }
            return css`
                color: ${c.neutral[900]};
                background: ${rgbaFromHex('#7543DC', 0.1)} !important;
            `;
        }}
    }

    @media (max-width: 1024px) {
        padding: 8px 14px;
        font-size: 14px;
    }

    @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 13px;
        max-width: 150px;
        &:first-of-type {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }
        &:last-of-type {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }
    }

    @media (max-width: 480px) {
        padding: 6px 10px;
        font-size: 12px;
        max-width: 120px;
    }
`;
